import { TreeNodeLike } from '../Types/Data.type';

export type Option = { value: string; label: string };


//ordinamento
export const sortByOrdine = <T extends { ordine?: number }>(a: T, b: T) =>
  (a.ordine ?? 0) - (b.ordine ?? 0);


/**
 * Ricostruisce l'albero se l'array è piatto (senza children)
 * oppure lo normalizza (assicurando children[] e ordinamenti).
 * Considera root sia parentID null che 0.
 */

type WithChildren = TreeNodeLike & { children: TreeNodeLike[] };
export function ensureTree(input: TreeNodeLike[]): TreeNodeLike[] {
  if (!Array.isArray(input) || input.length === 0) return [];

  const hasChildrenAlready = input.some(n => Array.isArray(n.children) && n.children.length > 0);
  if (hasChildrenAlready) {
    // Normalizza/ordina mantenendo children sempre come array
    const normalize = (nodes: TreeNodeLike[]): TreeNodeLike[] =>
      nodes
        .slice()
        .sort(sortByOrdine)
        .map(n => ({
          ...n,
          children: normalize(n.children ?? []),
        }));
    // Roots: parentID null o 0
    return normalize(input.filter(n => n.parentID === null || n.parentID === 0));
  }

  // Ricostruisco da lista piatta
  const byId = new Map<string, WithChildren>();
  input.forEach(n => {
    byId.set(String(n.ID), { ...n, children: [] });
  });

  const roots: WithChildren[] = [];
  byId.forEach(node => {
    const p = node.parentID;
    if (p === null || p === 0 || !byId.has(String(p))) {
      roots.push(node);
    } else {
      byId.get(String(p))!.children.push(node);
    }
  });

  // Accetta anche nodi senza children definiti e usa fallback []
  const sortDeep = (nodes: TreeNodeLike[]): TreeNodeLike[] =>
    nodes
      .slice()
      .sort(sortByOrdine)
      .map(n => ({
        ...n,
        children: sortDeep((n as any).children ?? []),
      }));

  return sortDeep(roots);
}



/**
 * Crea le opzioni da usare in <select>, con indentazione visuale.
 * - excludeId: evita che un nodo diventi padre di sé stesso (utile in edit)
 * - indent: stringa per l'indentazione (NBSP di default)
 * - getLabel: funzione per generare l'etichetta
 */
export function buildTreeOptions(
  list: TreeNodeLike[], { excludeId, indent = '\u00A0\u00A0', getLabel, }: 
  { excludeId?: number | string; indent?: string; getLabel?: (n: TreeNodeLike) => string; } = {}
): Option[] {
  const roots = ensureTree(list);
  const opts: Option[] = [];

  const labelFn =
    getLabel ??
    ((n: TreeNodeLike) => {
      const base = n.nome_it ?? n.nome ?? `#${n.ID}`;
      return base;
    });

  const visit = (node: TreeNodeLike, depth: number) => {
    if (excludeId && String(node.ID) === String(excludeId)) return;
    opts.push({
      value: String(node.ID),
      label: `${indent.repeat(depth)}${labelFn(node)}`,
    });
    (node.children ?? []).forEach(child => visit(child, depth + 1));
  };

  roots.forEach(r => visit(r, 0));
  return opts;
}



export function fmtDate(it: string) {
  if (!it) return '-'
  const s = it.replace(' ', 'T')
  const d = new Date(s)
  if (isNaN(d.getTime())) return it
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}


//format delle date con ora
export function fmtDateTime(it: string) {
  if (!it) return '-'
  const s = it.replace(' ', 'T')
  const d = new Date(s)
  if (isNaN(d.getTime())) return it
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`
}